import type { ApiEstimate } from "@/lib/api/types"

// Age + severity for an estimate — drives the small "aging" pill on rows and
// pipeline cards. The thresholds are deliberately quiet on the low end (a
// 2-hour-old estimate shouldn't shout) and escalate at the points where a
// service advisor would actually start worrying:
//   < 24h  : fresh — neutral pill ("3h ago")
//   24-72h : warm  — amber pill ("1d ago")
//   > 72h  : stale — red pill ("4d ago")
//
// Aging only applies to statuses where the estimate is *waiting* on the user —
// Pending and InProgress. Scheduled has its own date; Completed/Declined are
// done; Draft is a self-edit-in-progress state. We return `severity: 'none'`
// for those so callers don't render a pill.

export type AgeSeverity = "none" | "fresh" | "warm" | "stale"

export interface EstimateAge {
  hours: number
  label: string
  severity: AgeSeverity
}

const ACTIONABLE_STATUSES = new Set<ApiEstimate["status"]>([
  "Pending",
  "Confirmed",
  "InProgress",
])

const FRESH_HOURS = 24
const STALE_HOURS = 72

function formatLabel(hours: number): string {
  if (hours < 1) return "Just now"
  if (hours < 24) return `${Math.floor(hours)}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function severityFor(hours: number): AgeSeverity {
  if (hours < FRESH_HOURS) return "fresh"
  if (hours < STALE_HOURS) return "warm"
  return "stale"
}

export function getEstimateAge(estimate: ApiEstimate): EstimateAge {
  if (!ACTIONABLE_STATUSES.has(estimate.status)) {
    return { hours: 0, label: "", severity: "none" }
  }
  const created = new Date(estimate.createdAt).getTime()
  if (!Number.isFinite(created)) {
    return { hours: 0, label: "", severity: "none" }
  }
  const hours = (Date.now() - created) / 3_600_000
  return {
    hours,
    label: formatLabel(hours),
    severity: severityFor(hours),
  }
}
